import { useQueryClient } from '@tanstack/react-query';
import { type Event, createEvent as createEventModel } from 'models';
import { useMutation } from 'query';
import { handleAxiosError } from 'ui';
import type { ApiClientResponseError, ResponseErrorDataObject } from 'common/services/api';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: break circular dependency
import { getCalendarPrincipalEventsListQueryKey } from 'features/calendar/queries';
import { createEvent } from '../../services';
import type { EventCreateParticipant } from '../../types';
import { getNodeEventsQueryKey } from '../keys';

export type CreateEventData =
  | CreateCallEventData
  | CreateClinicalReviewEventData
  | CreateReminderEventData
  | CreateTelehealthVisitEventData
  | CreateVideoConferenceEventData
  | CreateWebinarEventData;

type CreateBaseEventData = {
  endTime: string;
  html?: string;
  name: string;
  participants: EventCreateParticipant[];
  startTime: string;
  text?: string;
};

type CreateCallEventData = {
  medicalGroup: string;
  medicalGroupName: string;
  type: 'call';
} & CreateBaseEventData;

type CreateChannelEventData = {
  featuredInChannels?: boolean;
  organization?: string;
  showInChannel?: boolean;
  tags?: string[];
};

type CreateClinicalReviewEventData = {
  medicalGroup: string;
  medicalGroupName: string;
  type: 'clinical_review';
} & CreateBaseEventData;

type CreateReminderEventData = {
  allDay?: boolean;
  distributionList?: DistributionListItem[];
  type: 'reminder';
} & CreateBaseEventData;

type CreateTelehealthVisitEventData = {
  medicalGroup: string;
  medicalGroupName: string;
  type: 'telehealth_visit';
} & CreateBaseEventData;

type CreateVideoConferenceEventData = {
  channel: CreateChannelEventData;
  distributionList?: DistributionListItem[];
  type: 'video_conference';
} & CreateBaseEventData;

type CreateWebinarEventData = {
  channel: CreateChannelEventData;
  distributionList?: DistributionListItem[];
  type: 'webinar';
} & CreateBaseEventData;

type DistributionListItem = {
  communityId: string;
  communityName: string;
};

const defaultMutateOnError = (error: ApiClientResponseError<ResponseErrorDataObject>) => {
  handleAxiosError(error, 'Failed to create event.');
};

export const useCreateEventMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Event, CreateEventData>({
    defaultMutateOnError,
    async mutationFn(data) {
      const response = await createEvent({
        body: { ...data },
        config: {
          params: {},
        },
      });

      return createEventModel(response.data);
    },
    async onSettled() {
      await queryClient.invalidateQueries({ queryKey: getNodeEventsQueryKey({}) });
      await queryClient.invalidateQueries({ queryKey: getCalendarPrincipalEventsListQueryKey({}) });
    },
  });
};
